import { useEffect, Suspense } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { ThemeProvider } from "@mui/system";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { APIProvider } from "@vis.gl/react-google-maps";
import theme from "./utils/theme";
import { OrgContext, UserContext } from "./utils/context";
import { isPendingProvider, isProviderMember, isProviderSignupInProgress } from "./utils/auth";
import { useParams } from "react-router-dom";
import {
  FirebaseProvider,
  useFirebase,
} from "./utils/contexts/FirebaseContext";
import { AlertProvider, useAlert } from "./contexts/AlertContext";
import { useUser } from "./hooks/useUser";
import GlobalAlert from "./components/GlobalAlert";
import Layout from "./components/Layout";
import Loading from "./components/reusable/Loading";
import { ModalProvider } from "./components/reusable/modals/modal_provider";
import RouteErrorBoundary from "./components/RouteErrorBoundary";
import { lazyWithRetry as lazy } from "./utils/lazyWithRetry";
import "./App.css";
import { setCurrentRouteBeforeLogin } from "./utils/api";
import { EDGE_RETRY_EVENT } from "./utils/requestRetry";
// Not lazy: it must render while the API, and possibly the network, is gone.
import ServiceDown from "./components/pages/ServiceDown";
import { useServiceStatus } from "./hooks/useServiceStatus";

// Lazy-loaded page components
const Login = lazy(() => import("./components/pages/Login"));
const Organisations = lazy(() => import("./components/pages/Organisation"));
const OrganizationList = lazy(() => import("./components/pages/Organisation/OrganizationList"));
const QuickcareDocumentRules = lazy(() => import("./components/pages/QuickcareDocumentRules"));
const AcecqaRegisterAdmin = lazy(() => import("./components/pages/AcecqaRegisterAdmin"));
const OrgCentreForm = lazy(() => import("./components/forms/OrgCentreForm"));
const CentreDetail = lazy(() => import("./components/pages/CentreDetail"));
const Shifts = lazy(() => import("./components/pages/Shifts"));
const ResetForm = lazy(() => import("./components/forms/ResetPassword"));
const SignupForm = lazy(() => import("./components/forms/SignupForm"));
const NotFound = lazy(() => import("./components/pages/NotFound"));
const Educators = lazy(() => import("./components/pages/Educators"));
const Compliance = lazy(() => import("./components/pages/Compliance"));
const Reports = lazy(() => import("./components/pages/Reports"));
const Logins = lazy(() => import("./components/pages/Logins"));
const Messages = lazy(() => import("./components/pages/Messages"));
const ShiftDetail = lazy(() => import("./components/pages/ShiftDetail/ShiftDetail"));
const EducatorDetail = lazy(() => import("./components/pages/EducatorDetail"));
const YourProfile = lazy(() => import("./components/pages/YourProfile"));
const EducatorAccount = lazy(() => import("./components/pages/EducatorAccount"));
const SignupSuccess = lazy(() => import("./components/pages/SignUpSuccess"));
const ErrorShiftPage = lazy(() => import("./components/pages/ShiftDetail/error_page"));
const DownloadPage = lazy(() => import("./pages/download/DownloadPage"));
const StripeWrapperPage = lazy(() => import("./components/pages/StripeWrapperPage"));
const SigningPage = lazy(() => import("./components/pages/SigningPage"));
const TempCertPage = lazy(() => import("./components/pages/TempCertPage"));
const TimeSheets = lazy(() => import("./components/pages/TimeSheets"));
const NationalRegister = lazy(() => import("./components/pages/NationalRegister"));
const ShiftGroups = lazy(() => import("./components/pages/ShiftGroups"));
const ShiftGroupDetail = lazy(() => import("./components/pages/ShiftGroups/ShiftGroupDetail"));
const AdminSendMessage = lazy(() => import("./components/pages/AdminSendMessage"));
const SupportEmail = lazy(() => import("./components/pages/SupportEmail"));
const Policies = lazy(() => import("./components/pages/Policies"));
const PendingApprovals = lazy(() => import("./components/pages/PendingApprovals"));
const AIDashboardPage = lazy(() => import("./components/ai-dashboard/AIDashboardPage"));
const SystemHealth = lazy(() => import("./components/pages/SystemHealth"));
const RefCheckEducatorPage = lazy(() => import("./pages/refCheck/RefCheckEducatorPage"));
const RefCheckRefereePage = lazy(() => import("./pages/refCheck/RefCheckRefereePage"));
const ScheduleInterviewPage = lazy(() => import("./pages/scheduleInterview/ScheduleInterviewPage"));
const ProviderSignupPage = lazy(() => import("./pages/providerSignup/ProviderSignupPage"));

// Create a client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (v5 renamed cacheTime -> gcTime)
    },
  },
});

// api.js fires this once a call has recovered from an upstream 500/503 (Firebase
// Hosting's plain-text "Internal Error", Cloud Run's 503): refetch only the
// queries that failed during that blip, through the now healthy path. Never a
// blanket refetch — that would be a burst of load right after the edge
// recovered — and a query already refetching is left to finish
// (cancelRefetch: false) rather than restarted. The retried call itself needs
// nothing: its retry already went past the HTTP cache. Module level, like the
// client, so it is registered once.
if (typeof window !== "undefined") {
  window.addEventListener(EDGE_RETRY_EVENT, () => {
    queryClient.invalidateQueries(
      { predicate: (query) => query.state.status === "error" },
      { cancelRefetch: false }
    );
  });
}
// Component to handle FCM token sending after login
function FCMTokenHandler({ currentUser, sendTokenCallback }) {
  const {
    fcmToken,
    notificationPermission,
    isSupported,
    messaging,
    requestNotificationPermission,
  } = useFirebase();

  useEffect(() => {
    if (currentUser && fcmToken) {
      sendTokenCallback(fcmToken);
    }
  }, [currentUser, fcmToken]);

  // Temporary debug component - only show when no token and user is logged in
  if (currentUser && !fcmToken && import.meta.env.MODE === "development") {
    return (
      <div
        style={{
          position: "fixed",
          bottom: "20px",
          left: "20px",
          backgroundColor: "#f8d7da",
          border: "1px solid #f5c6cb",
          borderRadius: "4px",
          padding: "10px",
          zIndex: 1000,
          fontSize: "12px",
        }}
      >
        <div>
          <strong>FCM Token Debug:</strong>
        </div>
        <div>Permission: {notificationPermission}</div>
        <div>Supported: {isSupported ? "Yes" : "No"}</div>
        <div>Messaging: {messaging ? "Available" : "Not Available"}</div>
        <button
          onClick={requestNotificationPermission}
          style={{
            marginTop: "8px",
            padding: "4px 8px",
            fontSize: "11px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "2px",
            cursor: "pointer",
          }}
        >
          Generate Token
        </button>
      </div>
    );
  }

  return null; // This component doesn't render anything
}

function ResetWrapper() {
  const { code } = useParams();
  return <ResetForm code={code} />;
}

function SignupWrapper() {
  const { secret } = useParams();
  return <SignupForm secret={secret} />;
}

function SignupSuccessWrapper() {
  const { orgInvite } = useParams();
  return <SignupSuccess orgInvite={orgInvite} />;
}

function WildcardRedirect() {
  const currentPath = window.location.href.replace(window.location.origin, '');
  setCurrentRouteBeforeLogin(currentPath);
  return <Navigate to="/login" />;
}

function RootElement({ loading, currentUser, isAdmin, userOrg, onLogout }) {
  if (loading || !currentUser) {
    return <Loading />;
  }
  if (isAdmin) return <Educators currentUser={currentUser} />;
  if (userOrg?.access === 'co_only') return <EducatorAccount onLogout={onLogout} currentUser={currentUser} />;
  if (userOrg && userOrg.access !== "educator") return <Shifts />;
  return <EducatorAccount onLogout={onLogout} currentUser={currentUser} />;
}

function AppContent() {
  const { setAlert } = useAlert();
  const { down: serviceDown } = useServiceStatus();

  const {
    onLoginSuccess,
    onLogout,
    sendFCMToken,
    currentUser,
    isAdmin,
    userOrg,
    isLoggedIn,
    isLoading: loading,
    logoutLoading,
    error,
  } = useUser();

  useEffect(() => {
    if (error && !serviceDown) {
      setAlert("Authentication failed. Please try logging in again.", "error");
    }
  }, [error, serviceDown, setAlert]);

  // QC-2927: two logins belong in the signup rather than the dashboard. A
  // provider whose organisation is still pending terms has no dashboard yet —
  // every /manage call would 403 ORG_PENDING_TERMS — and lands on the signup's
  // pending step ("Check again"). A person stamped as a provider signup with
  // no organisation yet (chose "provider" in the app, or started the web
  // flow) resumes where the signup's status says; without this they fell
  // into the educator account.
  const navigate = useNavigate();
  const pendingProvider = isLoggedIn && !isAdmin && (isPendingProvider(userOrg) || isProviderSignupInProgress(currentUser));

  // QC-2932: an owner can switch the Educators / Compliance surfaces off per
  // login. Hiding the nav entry (Layout.js) is not enough on its own — a
  // bookmark or a deep link would still render a page whose every request
  // 403s — so the routes redirect too. Both capabilities default ON, so only an
  // explicit false takes the page away, and admins are never restricted.
  // `.some()` matches the nav: a multi-org login keeps the page while any of
  // their orgs allows it, and the API enforces the per-organisation answer.
  const providerOrgs = currentUser?.organisations || [];
  const canExploreEducators =
    isAdmin || providerOrgs.some((o) => o.can_explore_educators !== false);
  const canViewCompliance =
    isAdmin || providerOrgs.some((o) => o.can_view_compliance !== false);
  useEffect(() => {
    if (pendingProvider) navigate('/provider-signup', { replace: true });
  }, [pendingProvider, navigate]);

  // The API is unreachable: replace the whole app, logged in or not, until
  // the health endpoint answers again (the store then reloads the page).
  if (serviceDown) {
    return <ServiceDown />;
  }
  if (pendingProvider) {
    return <Loading />;
  }

  return (
    <FirebaseProvider>
      <FCMTokenHandler
        currentUser={currentUser}
        sendTokenCallback={sendFCMToken}
      />
      <UserContext.Provider value={currentUser}>
        <OrgContext.Provider value={userOrg}>
          <Layout
              isLoggedIn={isLoggedIn}
              isAdmin={isAdmin}
              onLogout={onLogout}
              currentRole={userOrg ? userOrg.access : (isAdmin ? 'Admin' : null)}
              loading={loading}
              logOutLoading={logoutLoading}
              currentUser={currentUser}
            >
              <RouteErrorBoundary>
              <Suspense fallback={<Loading />}>
              <Routes>
                  {(isLoggedIn) ? (
                    <>
                      <Route
                        path="/"
                        element={
                          <RootElement
                            loading={loading}
                            currentUser={currentUser}
                            isAdmin={isAdmin}
                            userOrg={userOrg}
                            onLogout={onLogout}
                          />
                        }
                      />

                      <Route
                        path="/shifts"
                        element={userOrg?.access === 'co_only' ? <Navigate to='/' /> : <Shifts />}
                      />
                      <Route
                        path="/shifts/:shift_id"
                        element={
                          userOrg?.access === 'co_only'
                            ? <Navigate to='/' />
                            : <ShiftDetail currentUser={currentUser} />
                        }
                      />
                      <Route path="/shift-groups" element={<ShiftGroups />} />
                      <Route path="/shift-groups/:group_id" element={<ShiftGroupDetail />} />
                      <Route
                        path="/educators"
                        element={
                          canExploreEducators
                            ? <Educators currentUser={currentUser} />
                            : <Navigate to='/' />
                        }
                      />
                      <Route
                        path="/educators/:id"
                        element={
                          canExploreEducators
                            ? <EducatorDetail currentUser={currentUser} />
                            : <Navigate to='/' />
                        }
                      />
                      {/* Open to every provider-side role, support included —
                          a support login has their own documents to keep
                          current. co_only is excluded: it has its own profile
                          surface (EducatorAccount) and no drawer at all. */}
                      <Route
                        path="/profile"
                        element={
                          isProviderMember(userOrg) ? <YourProfile /> : <Navigate to='/' />
                        }
                      />
                      {/* Legacy path — the page moved to /profile. Kept as a
                          redirect so existing links and bookmarks still land. */}
                      <Route path="/your-profile" element={<Navigate to='/profile' replace />} />
                      <Route
                        path="/compliance"
                        element={
                          (userOrg?.access === 'co_only' || !canViewCompliance)
                            ? <Navigate to='/' />
                            : <Compliance currentUser={currentUser} view="org" />
                        }
                      />
                      {/* QC-2847 data-export reports. Same co_only gate as the
                          compliance dashboard — a CO-only member has no
                          workforce of their own to export. */}
                      <Route
                        path="/reports"
                        element={
                          // Owner-only (or Quickcare admin). Deep-linking as any
                          // other role would render a page whose every request
                          // 403s, so redirect instead.
                          (isAdmin || userOrg?.access === 'owner')
                            ? <Reports currentUser={currentUser} />
                            : <Navigate to='/' />
                        }
                      />
                      <Route
                        path="/compliance/centres/:centre_id"
                        element={
                          (userOrg?.access === 'co_only' || !canViewCompliance)
                            ? <Navigate to='/' />
                            : <Compliance currentUser={currentUser} view="centre" />
                        }
                      />
                      <Route
                        path="/compliance/blocked"
                        element={
                          (userOrg?.access === 'co_only' || !canViewCompliance)
                            ? <Navigate to='/' />
                            : <Compliance currentUser={currentUser} view="blocked" />
                        }
                      />
                      <Route
                        path="/compliance/pending-review"
                        element={
                          (userOrg?.access === 'co_only' || !canViewCompliance)
                            ? <Navigate to='/' />
                            : <Compliance currentUser={currentUser} view="pending-review" />
                        }
                      />
                      <Route
                        path="/compliance/at-risk"
                        element={
                          (userOrg?.access === 'co_only' || !canViewCompliance)
                            ? <Navigate to='/' />
                            : <Compliance currentUser={currentUser} view="at-risk" />
                        }
                      />
                      <Route
                        path="/compliance/incomplete"
                        element={
                          (userOrg?.access === 'co_only' || !canViewCompliance)
                            ? <Navigate to='/' />
                            : <Compliance currentUser={currentUser} view="incomplete" />
                        }
                      />
                      <Route path="/messages" element={<Messages />} />
                      <Route path="/timesheets" element={<TimeSheets />} />
                      <Route path="/pending-approvals" element={<PendingApprovals />} />
                      <Route path="/national-register" element={<NationalRegister />} />
                      <Route
                        path="/logins-manage"
                        element={
                          canExploreEducators
                            ? <Logins />
                            : <Navigate to='/' />
                        }
                      />
                      {/* QC-2922: a login's full profile IS the educator detail
                          page. The route is kept so existing links (and the
                          side panel's "View full profile") still work, and so
                          Back knows to return to Manage Logins. It renders the
                          same component as /educators/:id, so it carries the
                          same guard — otherwise it would be an unguarded alias
                          for a page the capability just took away. */}
                      <Route
                        path="/logins-manage/:id"
                        element={
                          canExploreEducators
                            ? <EducatorDetail currentUser={currentUser} />
                            : <Navigate to='/' />
                        }
                      />
                      <Route path="/policies" element={<Policies />} />
                      <Route
                        path="/organisation"
                        element={
                          isAdmin ? <OrganizationList /> : <Organisations />
                        }
                      />
                      <Route
                        path="/admin/document-rules"
                        element={
                          currentUser?.is_super_user
                            ? <QuickcareDocumentRules />
                            : <Navigate to="/" />
                        }
                      />
                      <Route
                        path="/admin/acecqa-register"
                        element={isAdmin ? <AcecqaRegisterAdmin /> : <Navigate to="/" />}
                      />
                      <Route
                        path="/organisations/:org_id"
                        element={<Organisations />}
                      />
                      <Route
                        path="/organisations/:org_id/add-centre"
                        element={<OrgCentreForm />}
                      />
                      <Route
                        path="/organisations/:org_id/centres/:centre_id/edit"
                        element={<OrgCentreForm />}
                      />
                      <Route
                        path="/organisations/:org_id/centres/:centre_id"
                        element={<CentreDetail />}
                      />
                      <Route
                        path="/admin-send-message"
                        element={isAdmin ? <AdminSendMessage /> : <Navigate to="/" />}
                      />
                      <Route
                        path="/support-email"
                        element={isAdmin ? <SupportEmail /> : <Navigate to="/" />}
                      />
                      <Route
                        path="/ai-dashboard"
                        element={isAdmin ? <AIDashboardPage /> : <Navigate to="/" />}
                      />
                      <Route
                        path="/health"
                        element={isAdmin ? <SystemHealth /> : <Navigate to="/" />}
                      />
                      {/* scope values mirror COMPLIANCE_SCOPE from
                          dashboard/src/components/pages/Compliance/utils/constants.js.
                          Kept as literals here to preserve the lazy-load of the
                          Compliance chunk. */}
                      <Route
                        path="/admin/compliance"
                        element={
                          isAdmin
                            ? <Compliance currentUser={currentUser} view="org" scope="system" />
                            : <Navigate to="/" />
                        }
                      />
                      <Route
                        path="/admin/compliance/blocked"
                        element={
                          isAdmin
                            ? <Compliance currentUser={currentUser} view="blocked" scope="system" />
                            : <Navigate to="/" />
                        }
                      />
                      <Route
                        path="/admin/compliance/pending-review"
                        element={
                          isAdmin
                            ? <Compliance currentUser={currentUser} view="pending-review" scope="system" />
                            : <Navigate to="/" />
                        }
                      />
                      <Route
                        path="/admin/compliance/at-risk"
                        element={
                          isAdmin
                            ? <Compliance currentUser={currentUser} view="at-risk" scope="system" />
                            : <Navigate to="/" />
                        }
                      />
                      <Route
                        path="/admin/compliance/incomplete"
                        element={
                          isAdmin
                            ? <Compliance currentUser={currentUser} view="incomplete" scope="system" />
                            : <Navigate to="/" />
                        }
                      />
                      <Route
                        path="/admin/compliance/orgs/:org_id"
                        element={
                          isAdmin
                            ? <Compliance currentUser={currentUser} view="org" scope="admin" />
                            : <Navigate to="/" />
                        }
                      />
                      <Route
                        path="/admin/compliance/orgs/:org_id/centres/:centre_id"
                        element={
                          isAdmin
                            ? <Compliance currentUser={currentUser} view="centre" scope="admin" />
                            : <Navigate to="/" />
                        }
                      />
                      <Route
                        path="/admin/compliance/orgs/:org_id/blocked"
                        element={
                          isAdmin
                            ? <Compliance currentUser={currentUser} view="blocked" scope="admin" />
                            : <Navigate to="/" />
                        }
                      />
                      <Route
                        path="/admin/compliance/orgs/:org_id/pending-review"
                        element={
                          isAdmin
                            ? <Compliance currentUser={currentUser} view="pending-review" scope="admin" />
                            : <Navigate to="/" />
                        }
                      />
                      <Route
                        path="/admin/compliance/orgs/:org_id/at-risk"
                        element={
                          isAdmin
                            ? <Compliance currentUser={currentUser} view="at-risk" scope="admin" />
                            : <Navigate to="/" />
                        }
                      />
                      <Route
                        path="/admin/compliance/orgs/:org_id/incomplete"
                        element={
                          isAdmin
                            ? <Compliance currentUser={currentUser} view="incomplete" scope="admin" />
                            : <Navigate to="/" />
                        }
                      />
                      <Route
                        path="/shifts/error"
                        element={<ErrorShiftPage />}
                      />
                      <Route path="/login" element={<Navigate to="/" />} />{" "}
                      <Route
                        path="/temp-educator-documents"
                        element={<TempCertPage />}
                      />
                      <Route path="*" element={<NotFound />} />
                    </>
                  ) : (
                    <>
                      <Route path="/login" element={<Login onLoginSuccess={onLoginSuccess} />} />
                      <Route path="*" element={<WildcardRedirect />} />
                    </>
                  )}
                  <Route
                    path="/signup-success/:orgInvite"
                    element={<SignupSuccessWrapper />}
                  />
                  <Route path="/signup/:secret" element={<SignupWrapper />} />
                  <Route path="/reset/:code" element={<ResetWrapper />} />
                  <Route path="/stripe" element={<StripeWrapperPage />} />
                  <Route path="/signing" element={<SigningPage />} />
                </Routes>
              </Suspense>
              </RouteErrorBoundary>
          </Layout>
          <GlobalAlert />
        </OrgContext.Provider>
      </UserContext.Provider>
    </FirebaseProvider>
  );
}

/**
 * Dispatcher kept at the App level so all routing lives in a single place
 * (addresses review comment on QC2591).
 *
 * Public ref-check forms are rendered here directly so that anonymous
 * referees bypass AppContent's auth shell (useUser / FirebaseProvider /
 * Layout). Everything else falls through to AppContent unchanged.
 */
function RouteDispatcher() {
  const { pathname } = useLocation();

  if (pathname.startsWith("/public/ref-check/")) {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route
              path="/public/ref-check/educator/:token"
              element={<RefCheckEducatorPage />}
            />
            <Route
              path="/public/ref-check/referee/:token"
              element={<RefCheckRefereePage />}
            />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  // Public app-download landing page — linked from marketing emails and SMS,
  // so anonymous visitors must be able to see it. It bypasses AppContent's
  // auth shell because that shell always calls `/me`, and the 401 handler in
  // api.js hard-redirects an anonymous visitor to /login.
  if (pathname === "/download") {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/download" element={<DownloadPage />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  // Public booking/reschedule page — authed by the ?token= in the URL, so it
  // bypasses AppContent's auth shell like the ref-check forms above.
  if (pathname === "/schedule-interview") {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route
              path="/schedule-interview"
              element={<ScheduleInterviewPage />}
            />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  // Self-serve provider signup (QC-2927) — public, creates its own session
  // through the OTP, so it must not sit inside the auth shell (which would
  // call `/me` and bounce an anonymous visitor to /login).
  if (pathname === "/provider-signup") {
    return (
      <RouteErrorBoundary>
        <Suspense fallback={<Loading />}>
          <Routes>
            <Route path="/provider-signup" element={<ProviderSignupPage />} />
          </Routes>
        </Suspense>
      </RouteErrorBoundary>
    );
  }

  return <AppContent />;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AlertProvider>
        <ThemeProvider theme={theme}>
          <ModalProvider>
            <APIProvider
              apiKey={import.meta.env.VITE_GOOGLE_MAPS_KEY || ''}
              libraries={['places']}
              version='weekly'
            >
              <Router>
                <RouteDispatcher />
              </Router>
            </APIProvider>
          </ModalProvider>
        </ThemeProvider>
      </AlertProvider>
    </QueryClientProvider>
  );
}

export default App;
